import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Card,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, ArrowBack, Delete, Edit, MedicalServices } from '@mui/icons-material';
import apiClient from '../lib/axios';

interface Specialty {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

const NAVY = '#0D1B35';
const BLUE = '#0066CC';

export const SpecialtyList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedSpecialty, setSelectedSpecialty] = useState<Specialty | null>(null);
  const [specialtyName, setSpecialtyName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: specialties, isLoading, error: queryError } = useQuery({
    queryKey: ['specialties'],
    queryFn: async () => {
      const { data } = await apiClient.get<Specialty[]>('/specialties');
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (name: string) => {
      await apiClient.post('/specialties', { name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specialties'] });
      setCreateDialogOpen(false);
      setSpecialtyName('');
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to create specialty');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      await apiClient.put(`/specialties/${id}`, { name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specialties'] });
      setEditDialogOpen(false);
      setSelectedSpecialty(null);
      setSpecialtyName('');
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update specialty');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/specialties/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specialties'] });
      setDeleteDialogOpen(false);
      setSelectedSpecialty(null);
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to delete specialty');
    },
  });

  const handleCreateClick = () => {
    setSpecialtyName('');
    setError(null);
    setCreateDialogOpen(true);
  };

  const handleEditClick = (specialty: Specialty) => {
    setSelectedSpecialty(specialty);
    setSpecialtyName(specialty.name);
    setError(null);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (specialty: Specialty) => {
    setSelectedSpecialty(specialty);
    setError(null);
    setDeleteDialogOpen(true);
  };

  const handleCreateConfirm = () => {
    if (specialtyName.trim().length >= 2) {
      createMutation.mutate(specialtyName.trim());
    }
  };

  const handleEditConfirm = () => {
    if (selectedSpecialty && specialtyName.trim().length >= 2) {
      updateMutation.mutate({ id: selectedSpecialty.id, name: specialtyName.trim() });
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedSpecialty) {
      deleteMutation.mutate(selectedSpecialty.id);
    }
  };

  const allSpecialties = specialties ?? [];

  return (
    <Box>
      {/* Hero */}
      <Box
        sx={{
          borderRadius: 4,
          bgcolor: NAVY,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
          p: { xs: '28px 24px', sm: '40px 44px' },
          mb: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', width: 260, height: 260, borderRadius: '50%', bgcolor: 'rgba(0,102,204,0.12)', top: -80, right: -60, pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', width: 140, height: 140, borderRadius: '50%', bgcolor: 'rgba(0,102,204,0.08)', bottom: -50, left: '35%', pointerEvents: 'none' }} />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <IconButton size="small" onClick={() => navigate('/admin/dashboard')} sx={{ color: 'rgba(255,255,255,0.5)', p: 0.5 }}>
                <ArrowBack fontSize="small" />
              </IconButton>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase' }}>
                ESPECIALIDADES
              </Typography>
            </Box>
            <Typography variant="h2" sx={{ color: '#fff', mb: 0.5 }}>
              Especialidades Médicas
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: { xs: 13, sm: 14 } }}>
              {allSpecialties.length} especialidades registradas
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateClick}
            sx={{ bgcolor: BLUE, flexShrink: 0, mt: { xs: 0, sm: 1 }, '&:hover': { bgcolor: '#0052a3' } }}
          >
            {isMobile ? 'Agregar' : 'Agregar Especialidad'}
          </Button>
        </Box>
      </Box>

      {queryError && (
        <Alert severity="error" sx={{ mb: 2 }}>Failed to load specialties. Please try again.</Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        /* Mobile card list */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {allSpecialties.length > 0 ? (
            allSpecialties.map((specialty) => (
              <Card key={specialty.id} elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: `${BLUE}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: BLUE, flexShrink: 0 }}>
                      <MedicalServices sx={{ fontSize: 18 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: NAVY, fontSize: 15 }}>{specialty.name}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {new Date(specialty.createdAt).toLocaleDateString()}
                    </Typography>
                    <Box>
                      <IconButton size="small" color="primary" onClick={() => handleEditClick(specialty)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteClick(specialty)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Card>
            ))
          ) : (
            <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
              <MedicalServices sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
              <Typography>No specialties found. Create one to get started.</Typography>
            </Box>
          )}
        </Box>
      ) : (
        /* Desktop table */
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allSpecialties.length > 0 ? (
                  allSpecialties.map((specialty) => (
                    <TableRow key={specialty.id}>
                      <TableCell>
                        <Typography variant="body1" fontWeight={500}>{specialty.name}</Typography>
                      </TableCell>
                      <TableCell>{new Date(specialty.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="primary" onClick={() => handleEditClick(specialty)}>
                          <Edit />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(specialty)}>
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography color="text.secondary" py={4}>
                        No specialties found. Create one to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Specialty</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the name of the medical specialty.
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            autoFocus
            label="Specialty Name"
            fullWidth
            value={specialtyName}
            onChange={(e) => setSpecialtyName(e.target.value)}
            error={specialtyName.length > 0 && specialtyName.length < 2}
            helperText={specialtyName.length > 0 && specialtyName.length < 2 ? 'Name must be at least 2 characters' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateConfirm}
            variant="contained"
            disabled={specialtyName.trim().length < 2 || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Specialty</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Update the specialty name.
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            autoFocus
            label="Specialty Name"
            fullWidth
            value={specialtyName}
            onChange={(e) => setSpecialtyName(e.target.value)}
            error={specialtyName.length > 0 && specialtyName.length < 2}
            helperText={specialtyName.length > 0 && specialtyName.length < 2 ? 'Name must be at least 2 characters' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditConfirm}
            variant="contained"
            disabled={specialtyName.trim().length < 2 || updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Specialty</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <DialogContentText>
            Are you sure you want to delete <strong>{selectedSpecialty?.name}</strong>?
            {!error && ' This action cannot be undone.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
